import { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Result, Button } from '../ui';
import { useAuthStore } from '../store/auth';
import { RedirectToLogin } from './LoginModal';

/**
 * Route-level RBAC guard.
 *
 * - Not logged in        → home + open the login modal (no /login page).
 * - Logged in, no perm   → render a 403 (NOT a redirect, to avoid loops with
 *                          RoleHome when the landing page itself is gated).
 * - `anyOf` empty/absent → just requires authentication (self-service pages).
 *
 * SUPER_ADMIN and wildcard permissions always pass (handled in hasPermission).
 */
export function RequirePerm({ anyOf, denyRoles, children }: { anyOf?: string[]; denyRoles?: string[]; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const hasAny = useAuthStore((s) => s.hasAnyPermission);
  const location = useLocation();
  const navigate = useNavigate();

  if (!user) return <RedirectToLogin from={location.pathname + location.search} />;

  // Role denial takes precedence over the permission check: a staff page can be closed to a role even
  // when that role holds a broad/shared permission that also gates it — e.g. a retailer holds STOCK.VIEW
  // (for the catalog's in-stock indicator) but must NOT reach the staff Stock page by URL.
  const roleDenied = !!denyRoles && !!user.role && denyRoles.includes(user.role);
  if (roleDenied || !hasAny(anyOf)) {
    return (
      <Result
        status="403"
        title="403 — Not authorised"
        subTitle="Your role does not have permission to view this page."
        extra={
          <Button variant="primary" onClick={() => navigate('/home')}>
            Go to my home
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
